// src/components/maintenance/MaintenanceManagement.js
// Updated with sidebar navigation and consistent styling

import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';
import MaintenanceTaskList from './MaintenanceTaskList';
import MaintenanceTaskForm from './MaintenanceTaskForm';
import MaintenanceStats from './MaintenanceStats';  
import MaintenanceCalendar from './MaintenanceCalendar';
import MaintenanceTaskDetail from './MaintenanceTaskDetail';
import '../../pages/UserDashboard.css';
import './MaintenanceManagement.css';

function MaintenanceManagement({ userData, onBack }) {
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('list');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    priority: 'all',
    dateRange: 'all',
    vendor: 'all'
  });
  
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    overdueTask: 0,
    monthlySpend: 0,
    upcomingThisWeek: 0
  });

  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
  const isSuperAdmin = userData?.role === 'superadmin';

  useEffect(() => {
    fetchInitialData();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [darkMode]);

  useEffect(() => {
    if (categories.length > 0 || vendors.length > 0) {
      fetchTasks();
    }
  }, [filters, categories, vendors]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch categories
      const { data: categoriesData } = await supabase
        .from('maintenance_categories')
        .select('*')
        .order('display_order');
      
      setCategories(categoriesData || []);

      // Fetch vendors
      const { data: vendorsData } = await supabase
        .from('service_vendors')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      setVendors(vendorsData || []);

      // Try to fetch templates
      try {
        const { data: templatesData } = await supabase
          .from('maintenance_templates')
          .select('*');
        
        if (templatesData) {
          setTemplates(templatesData);
        }
      } catch (error) {
        console.log('Templates table might not exist');
      }

      await fetchTasks();
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      let query = supabase
        .from('maintenance_tasks')
        .select('*')
        .order('scheduled_date', { ascending: true });

      // Apply filters
      if (filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.category !== 'all') {
        query = query.eq('category_id', filters.category);
      }
      if (filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }
      if (filters.vendor !== 'all') {
        query = query.eq('assigned_vendor_id', filters.vendor);
      }

      // Date range filter
      const today = new Date();
      if (filters.dateRange === 'today') {
        query = query.eq('scheduled_date', today.toISOString().split('T')[0]);
      } else if (filters.dateRange === 'week') {
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);
        query = query.gte('scheduled_date', today.toISOString().split('T')[0])
                     .lte('scheduled_date', weekEnd.toISOString().split('T')[0]);
      } else if (filters.dateRange === 'month') {
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        query = query.gte('scheduled_date', today.toISOString().split('T')[0])
                     .lte('scheduled_date', monthEnd.toISOString().split('T')[0]);
      }

      const { data: tasksData, error } = await query;

      if (!error) {
        // Process tasks with joins
        const processedTasks = (tasksData || []).map(task => {
          const isOverdue = new Date(task.scheduled_date) < today && 
                           task.status !== 'completed' && 
                           task.status !== 'cancelled';
          
          const category = categories.find(cat => cat.id === task.category_id);
          const vendor = vendors.find(v => v.id === task.assigned_vendor_id);
          
          return { 
            ...task, 
            isOverdue,
            maintenance_categories: category || null,
            service_vendors: vendor || null
          };
        });

        setTasks(processedTasks);
        calculateStats(processedTasks);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
      calculateStats([]);
    }
  };

  const calculateStats = (taskData) => {
    const today = new Date();
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const stats = {
      totalTasks: taskData.length,
      completedTasks: taskData.filter(t => t.status === 'completed').length,
      pendingTasks: taskData.filter(t => t.status === 'scheduled').length,
      overdueTask: taskData.filter(t => t.isOverdue).length,
      monthlySpend: taskData
        .filter(t => {
          const taskDate = new Date(t.completed_date || t.scheduled_date);
          return taskDate.getMonth() === currentMonth && 
                 taskDate.getFullYear() === currentYear &&
                 t.actual_cost;
        })
        .reduce((sum, t) => sum + parseFloat(t.actual_cost || 0), 0),
      upcomingThisWeek: taskData.filter(t => {
        const taskDate = new Date(t.scheduled_date);
        return taskDate >= today && taskDate <= weekEnd && t.status === 'scheduled';
      }).length
    };

    setStats(stats);
  };

  const handleAddTask = async (taskData) => {
    try {
      const { enable_reminder, ...taskDataToSave } = taskData;
      
      // Clean up empty values
      if (taskDataToSave.amc_end_date === '') {
        taskDataToSave.amc_end_date = null;
      }
      
      if (taskDataToSave.recurrence_pattern && taskDataToSave.recurrence_pattern.endDate === '') {
        taskDataToSave.recurrence_pattern.endDate = null;
      }
      
      // Calculate next occurrence for recurring tasks
      let nextOccurrence = null;
      if (taskDataToSave.task_type === 'recurring' && taskDataToSave.recurrence_pattern) {
        nextOccurrence = calculateNextOccurrence(
          taskDataToSave.scheduled_date, 
          taskDataToSave.recurrence_pattern
        );
      }

      const finalTaskData = {
        title: taskDataToSave.title,
        description: taskDataToSave.description || null,
        category_id: taskDataToSave.category_id,
        task_type: taskDataToSave.task_type || 'one_time',
        priority: taskDataToSave.priority || 'medium',
        status: taskDataToSave.status || 'scheduled',
        scheduled_date: taskDataToSave.scheduled_date,
        scheduled_time: taskDataToSave.scheduled_time || null,
        duration_hours: parseInt(taskDataToSave.duration_hours) || 1,
        assigned_vendor_id: taskDataToSave.assigned_vendor_id || null,
        estimated_cost: parseFloat(taskDataToSave.estimated_cost) || 0,
        actual_cost: 0,
        notes: taskDataToSave.notes || null,
        is_amc: taskDataToSave.is_amc || false,
        amc_document_url: taskDataToSave.amc_document_url || null,
        amc_end_date: taskDataToSave.amc_end_date || null,
        recurrence_pattern: taskDataToSave.recurrence_pattern || null,
        next_occurrence: nextOccurrence,
        created_by: userData.id,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('maintenance_tasks')
        .insert([finalTaskData])
        .select();

      if (error) throw error;

      setShowAddModal(false);
      setTimeout(() => {
        fetchTasks();
      }, 500);
      
      alert('Maintenance task created successfully!');
    } catch (error) {
      console.error('Error adding task:', error);
      alert('Failed to add task: ' + error.message);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const { error } = await supabase
        .from('maintenance_tasks')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId);

      if (error) throw error;

      await fetchTasks();
      alert('Task updated successfully!');
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task: ' + error.message);
    }
  };

  const handleCompleteTask = async (task, completionData) => {
    try {
      const { error } = await supabase
        .from('maintenance_tasks')
        .update({
          status: 'completed',
          completed_date: new Date().toISOString(),
          completed_by: userData.id,
          completion_notes: completionData.notes,
          actual_cost: parseFloat(completionData.cost) || 0,
          completion_images: completionData.images || [],
          updated_at: new Date().toISOString()
        })
        .eq('id', task.id);

      if (error) throw error;

      // If recurring, create next occurrence
      if (task.task_type === 'recurring' && task.recurrence_pattern) {
        await createNextRecurrence(task);
      }

      await fetchTasks();
      alert('Task completed successfully!');
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Failed to complete task: ' + error.message);
    }
  };

  const calculateNextOccurrence = (currentDate, pattern) => {
    const date = new Date(currentDate);
    
    switch (pattern.type) {
      case 'daily':
        date.setDate(date.getDate() + (pattern.interval || 1));
        break;
      case 'weekly':
        date.setDate(date.getDate() + (pattern.interval || 1) * 7);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + (pattern.interval || 1));
        if (pattern.dayOfMonth) {
          date.setDate(pattern.dayOfMonth);
        }
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + (pattern.interval || 1));
        break;
      default:
        return null;
    }
    
    return date.toISOString().split('T')[0];
  };

  const createNextRecurrence = async (task) => {
    const nextDate = calculateNextOccurrence(task.scheduled_date, task.recurrence_pattern);
    
    if (nextDate && (!task.end_date || nextDate <= task.end_date)) {
      const newTask = {
        ...task,
        scheduled_date: nextDate,
        status: 'scheduled',
        actual_cost: 0,
        completed_date: null,
        completed_by: null,
        completion_notes: null,
        created_at: new Date().toISOString()
      };

      delete newTask.id;

      const { error } = await supabase
        .from('maintenance_tasks')
        .insert([newTask]);

      if (!error) {
        console.log('Next recurrence created for:', nextDate);
      }
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!isSuperAdmin) {
      alert('Only superadmin can delete tasks');
      return;
    }

    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        const { error } = await supabase
          .from('maintenance_tasks')
          .delete()
          .eq('id', taskId);

        if (error) throw error;

        await fetchTasks();
        alert('Task deleted successfully!');
      } catch (error) {
        console.error('Error deleting task:', error);
        alert('Failed to delete task: ' + error.message);
      }
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className={`dashboard-container maintenance-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className={`menu-item ${activeView === 'list' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('list');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📋</span>
            <span className="menu-label">Task List</span>
          </button>
          
          <button
            className={`menu-item ${activeView === 'calendar' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('calendar');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📅</span>
            <span className="menu-label">Calendar</span>
          </button>
          
          <button
            className={`menu-item ${activeView === 'stats' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('stats');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📊</span>
            <span className="menu-label">Analytics</span>
          </button>
          
          {isAdmin && (
            <>
              <div className="sidebar-divider"></div>
              <button
                className="menu-item"
                onClick={() => {
                  setShowAddModal(true);
                  closeMobileMenu();
                }}
              >
                <span className="menu-icon">➕</span>
                <span className="menu-label">Add Task</span>
              </button>
            </>
          )}
          
          <div className="sidebar-divider"></div>
          
          <div className="filter-section">
            <label className="filter-label">Quick Filters</label>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
            
            <select
              value={filters.priority}
              onChange={(e) => setFilters({...filters, priority: e.target.value})}
              className="filter-select"
            >
              <option value="all">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">🔧 Maintenance</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="maintenance-header">
            <h2 className="page-title">🔧 Maintenance Management</h2>
            {isAdmin && (
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary"
              >
                + Add Task
              </button>
            )}
          </div>

          {/* Stats Cards */}
          <MaintenanceStats stats={stats} />

          {/* Content Area */}
          {loading ? (
            <div className="dashboard-card">
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading maintenance tasks...</p>
              </div>
            </div>
          ) : (
            <>
              {activeView === 'list' && (
                <MaintenanceTaskList
                  tasks={tasks}
                  categories={categories}
                  vendors={vendors}
                  filters={filters}
                  onFilterChange={setFilters}
                  onTaskClick={(task) => {
                    setSelectedTask(task);
                    setShowDetailModal(true);
                  }}
                  onUpdateTask={handleUpdateTask}
                  onCompleteTask={handleCompleteTask}
                  onDeleteTask={handleDeleteTask}
                  isAdmin={isAdmin}
                  isSuperAdmin={isSuperAdmin}
                />
              )}

              {activeView === 'calendar' && (
                <MaintenanceCalendar
                  tasks={tasks}
                  onTaskClick={(task) => {
                    setSelectedTask(task);
                    setShowDetailModal(true);
                  }}
                  onDateClick={(date) => {
                    if (isAdmin) {
                      setShowAddModal(true);
                    }
                  }}
                />
              )}

              {activeView === 'stats' && (
                <div className="dashboard-card">
                  <h3>Maintenance Analytics</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Detailed analytics and reports will be displayed here.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {showAddModal && (
        <MaintenanceTaskForm
          categories={categories}
          vendors={vendors}
          templates={templates}
          onSave={handleAddTask}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showDetailModal && selectedTask && (
        <MaintenanceTaskDetail
          task={selectedTask}
          onUpdate={handleUpdateTask}
          onComplete={handleCompleteTask}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTask(null);
          }}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}

export default MaintenanceManagement;